import {PointPlacemark} from '@logistics-frontend/blocks/lpc/map'
import {Point} from '@yandex-taxi/js-integration-api'
import {useRoute} from '@yandex-taxi/react-integration-api'
import {useMapContext, useMapUtil} from '@yandex-taxi/react-ymaps'
import {Props as PlacemarkProps} from '@yandex-taxi/react-ymaps/build/main/components/placemark'
import React, {useCallback, useMemo, useRef} from 'react'

import {BOUNDS_ANIMATION, MAX_ZOOM, PLACEMARK_ZOOM, WITHOUT_ANIMATION} from '../../components/consts'
import {useMapAddress as useAddress, useShouldBoundsSettings} from '../../components/map-bounds'
import {getCoords} from '../utils'

type Props = PlacemarkProps & {
  index: number
}

type DraftPointsProps = {
  setBounds: (newBounds: Point[]) => void
}

const DraftPoints: React.FC<DraftPointsProps> = ({setBounds}) => {
  const shouldBounds = useShouldBoundsSettings()
  const [{value: route = []}] = useRoute()
  // костыль нужен для того чтобы при маунте пины отрисовывались без лишней анимации
  const firstRender = useRef(false)
  const loading = useRef(false)
  const util = useMapUtil()
  const map = useMapContext()
  const icons = route.reduce<React.ReactNode[]>((acc, point, index) => {
    const coords = getCoords(point)

    if (coords) {
      acc.push(<DraftPoint key={index} coords={coords} index={index} />)
    }

    return acc
  }, [])

  const points = useMemo(
    () => (shouldBounds ? route.map(getCoords).filter(coords => !!coords) : undefined),
    [route, shouldBounds],
  )

  const checkZoomRange = useCallback(
    (bounds: Point[]) => {
      const {zoom} = util.bounds.getCenterAndZoom(bounds, map.container.getSize(), undefined, {
        margin: map.margin._margin,
      })
      if (zoom > MAX_ZOOM) {
        map.setZoom(MAX_ZOOM)
      }
    },
    [map, util],
  )

  React.useEffect(() => {
    const animation = firstRender.current ? BOUNDS_ANIMATION : WITHOUT_ANIMATION
    if (points && !loading.current) {
      if (points.length > 1) {
        loading.current = true
        const bounds = util.bounds.fromPoints(points)
        setBounds(bounds)
        map.setBounds(bounds, animation).then(() => (loading.current = false))
        if (!firstRender.current) {
          checkZoomRange(bounds)
        }
      } else if (points[0] && !util.math.areEqual(points[0], map.getCenter())) {
        loading.current = true
        const zoom = firstRender.current ? map.getZoom() : PLACEMARK_ZOOM
        map.setCenter(points[0], zoom, animation).then(() => (loading.current = false))
      }
    }
    firstRender.current = true
  }, [util, points, map, checkZoomRange, setBounds])

  return <>{icons}</>
}

const DraftPoint: React.FC<Props> = props => {
  const [, {setValue}] = useAddress(props.index)
  const dragEndHandler = useCallback(
    e => {
      const coords = e.get('target').geometry.getCoordinates()
      setValue(coords)
    },
    [setValue],
  )

  return <PointPlacemark {...props} coords={props.coords} onDragend={dragEndHandler} draggable />
}

export default DraftPoints
